/* @twid_ai_start */
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  CircularProgress,
  Paper,
  InputBase,
  IconButton,
  Chip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Grow
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfluencerCard from './InfluencerCard';
import { loadInfluencerData } from '../utils/dataLoader';

const Dashboard = ({ searchTerm: globalSearchTerm }) => {
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [selectedCities, setSelectedCities] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedGender, setSelectedGender] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Combine global and local search terms
  const searchTerm = globalSearchTerm || localSearchTerm;

  // Load data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await loadInfluencerData();
        setInfluencers(data);
        
        // Extract unique cities
        const cities = [...new Set(data
          .filter(item => item.data.city)
          .map(item => item.data.city))];
        setAvailableCities(cities);
        
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle city selection
  const handleCityClick = (city) => {
    setSelectedCities(prev => 
      prev.includes(city)
        ? prev.filter(c => c !== city)
        : [...prev, city]
    );
  };

  // Handle gender selection
  const handleGenderChange = (event, newGender) => {
    setSelectedGender(newGender);
  };

  // Filter data based on search term, selected cities, and gender
  const filteredData = influencers.filter(item => {
    if (!searchTerm && selectedCities.length === 0 && !selectedGender) return true;
    
    const { data } = item;
    const matchesSearch = !searchTerm || (
      (data.name && data.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (data.phone_number && data.phone_number.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    const matchesCity = selectedCities.length === 0 || 
      (data.city && selectedCities.includes(data.city));
    
    const matchesGender = !selectedGender || 
      (data.gender && data.gender === selectedGender);
    
    return matchesSearch && matchesCity && matchesGender;
  });

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '70vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading influencers...
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '70vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography color="error" variant="h6">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: 3, 
          mb: 4,
          px: isMobile ? 2 : 3
        }}
      >
        {/* Search Bar */}
        <Zoom in timeout={500}>
          <Paper
            component="form"
            sx={{ 
              p: '2px 4px', 
              display: 'flex', 
              alignItems: 'center', 
              width: '100%', 
              mb: 3,
              borderRadius: 2,
              boxShadow: theme.shadows[2],
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <InputBase
              sx={{ 
                ml: 1, 
                flex: 1,
                fontSize: isMobile ? '0.9rem' : '1rem'
              }}
              placeholder="Search by name or phone number..."
              value={localSearchTerm}
              onChange={(e) => setLocalSearchTerm(e.target.value)}
            />
            <IconButton 
              type="button" 
              sx={{ 
                p: '10px',
                color: theme.palette.primary.main,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)'
                }
              }} 
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
          </Paper>
        </Zoom>

        {/* Filters Section */}
        <Grow in timeout={500} style={{ transitionDelay: '200ms' }}>
          <Box sx={{ mb: 3 }}>
            {/* Gender Filter */}
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}
              >
                Filter by Gender:
              </Typography>
              <ToggleButtonGroup
                value={selectedGender}
                exclusive
                onChange={handleGenderChange}
                aria-label="gender filter"
                size={isMobile ? "small" : "medium"}
                sx={{
                  '& .MuiToggleButton-root': {
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.1)',
                      transform: 'scale(1.05)'
                    }
                  }
                }}
              >
                <ToggleButton value="MALE" aria-label="male">
                  Male
                </ToggleButton>
                <ToggleButton value="FEMALE" aria-label="female">
                  Female
                </ToggleButton>
                <ToggleButton value="" aria-label="all">
                  All
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* City Filters */}
            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{
                  fontSize: isMobile ? '0.9rem' : '1rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary
                }}
              >
                Filter by City:
              </Typography>
              <Stack 
                direction="row" 
                spacing={1} 
                flexWrap="wrap" 
                useFlexGap
                sx={{
                  '& .MuiChip-root': {
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }
                }}
              >
                {availableCities.map((city) => (
                  <Chip
                    key={city}
                    label={city}
                    onClick={() => handleCityClick(city)}
                    color={selectedCities.includes(city) ? "primary" : "default"}
                    size={isMobile ? "small" : "medium"}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Grow>

        {/* Display number of results */}
        <Fade in timeout={500} style={{ transitionDelay: '400ms' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2,
              fontSize: isMobile ? '0.9rem' : '1rem',
              color: theme.palette.text.secondary
            }}
          >
            Showing {filteredData.length} influencers
          </Typography>
        </Fade>

        {/* No results message */}
        {filteredData.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            my: 5,
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{
                fontSize: isMobile ? '1rem' : '1.25rem'
              }}
            >
              No results found
            </Typography>
          </Box>
        )}

        {/* Cards Grid */}
        <Grid 
          container 
          spacing={isMobile ? 1 : 2}
          sx={{
            '& .MuiGrid-item': {
              transition: 'all 0.3s ease-in-out'
            }
          }}
        >
          {filteredData.map((item, index) => (
            <Grid 
              item 
              xs={12} 
              sm={6} 
              md={4} 
              lg={3} 
              xl={2} 
              key={item.filename}
            >
              <InfluencerCard data={item} index={index} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Fade>
  );
};

export default Dashboard;
/* @twid_ai_end */ 